import {
	type Dispatch,
	type FC,
	type ReactNode,
	type RefObject,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useBeforeUnload, useBlocker } from "react-router"
import styled, { css, keyframes } from "styled-components"
import randomUUID from "uuid-random"

import BaseButton from "~/components/Button"
import { CloseIcon } from "~/components/Icon"
import { useAlert } from "~/contexts/AlertContext"
import { componentBorderColor, dangerColor, successColor, textColor } from "~/utilities/styles"

import { ToastContext, type ToastContextValue, UnsavedChangesOpenContext } from "."

interface UnsavedChangesData {
	isOpen: boolean
	isSaving: boolean
	saveLabel?: string
	canDiscard: boolean
}

interface UnsavedChangesHandlers {
	onSave(): Promise<boolean>
	onDiscard?(): void
}

const toastDurations = { success: 4000, error: 10_000 }
const maxVisibleToasts = 3

// The bar's offset from the bottom of the viewport plus its height and some breathing room.
const unsavedChangesClearance = 160

const ToastContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [toasts, setToasts] = useState<
		{ id: string; text: string; type: "success" | "error"; isExiting?: boolean }[]
	>([])
	const [unsavedChangesData, setUnsavedChangesData] = useState<UnsavedChangesData>()
	// Pages call setUnsavedChanges from effects on every render to refresh the save handler. Keeping the handlers in a
	// ref and returning the current state when nothing visible changed avoids scheduling a render per keystroke, which
	// React treats as an infinite update loop (error 185) when the keystrokes outpace the scheduled renders.
	const unsavedChangesHandlers = useRef<UnsavedChangesHandlers>(null)

	const isUnsavedChangesOpen = unsavedChangesData?.isOpen ?? false

	const clearUnsavedChanges = useCallback(() => {
		setUnsavedChangesData(current => (current?.isOpen ? { ...current, isOpen: false } : current))
	}, [])

	const openUnsavedChanges = useCallback((handlers: UnsavedChangesHandlers, saveLabel?: string) => {
		unsavedChangesHandlers.current = handlers
		const canDiscard = handlers.onDiscard !== undefined
		setUnsavedChangesData(current =>
			current?.isOpen && current.saveLabel === saveLabel && current.canDiscard === canDiscard
				? current
				: { isOpen: true, isSaving: false, saveLabel, canDiscard },
		)
	}, [])

	// The unsaved changes bar is fixed over the bottom of the page, so reserve room for it while it is open. Otherwise
	// fields at the end of a page sit underneath it, and scrolling them into view leaves them covered.
	useEffect(() => {
		if (!isUnsavedChangesOpen) return
		document.body.style.paddingBottom = `${unsavedChangesClearance}px`
		document.documentElement.style.scrollPaddingBottom = `${unsavedChangesClearance}px`
		return () => {
			document.body.style.paddingBottom = ""
			document.documentElement.style.scrollPaddingBottom = ""
		}
	}, [isUnsavedChangesOpen])

	const alert = useAlert()
	const blocker = useBlocker(isUnsavedChangesOpen)
	const isBlockerPromptOpen = useRef(false)
	useEffect(() => {
		if (blocker.state !== "blocked" || isBlockerPromptOpen.current) return
		isBlockerPromptOpen.current = true

		alert
			.confirm(
				"Discard unsaved changes",
				"You have unsaved changes. Leaving this page will discard them. Are you sure?",
				{ confirmLabel: "Discard changes" },
			)
			.then(dialog => {
				isBlockerPromptOpen.current = false
				if (!dialog.result) {
					blocker.reset()
					return
				}
				dialog.close()
				clearUnsavedChanges()
				blocker.proceed()
			})
	}, [blocker, alert, clearUnsavedChanges])

	const handleUnload = useCallback(
		(event: BeforeUnloadEvent) => {
			if (!isUnsavedChangesOpen) return

			event.preventDefault()
			const message =
				"You have unsaved changes. Navigating away from the page will discard the changes. Are you sure?"
			event.returnValue = message
			return message
		},
		[isUnsavedChangesOpen],
	)
	useBeforeUnload(handleUnload, { capture: true })

	// The ref is the source of truth, updated synchronously, so toasts created before React renders still see each
	// other for deduplication and the visible cap.
	const toastsRef = useRef(toasts)
	const updateToasts = useCallback((update: (current: typeof toasts) => typeof toasts) => {
		toastsRef.current = update(toastsRef.current)
		setToasts(toastsRef.current)
	}, [])
	const toastTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
	// Toasts held open by hover or focus, with the reasons that hold them.
	const pausedToasts = useRef(new Map<string, Set<"hover" | "focus">>())
	// Screen readers hear toasts through these always-mounted live regions, since a region that appears together with
	// its content is often not announced.
	const [announcements, setAnnouncements] = useState({ polite: "", assertive: "" })

	const announce = useCallback((politeness: "polite" | "assertive", text: string) => {
		// Clearing first makes a repeated message count as a change.
		setAnnouncements(current => ({ ...current, [politeness]: "" }))
		setTimeout(() => setAnnouncements(current => ({ ...current, [politeness]: text })), 100)
	}, [])

	const dismissToast = useCallback(
		(id: string) => {
			clearTimeout(toastTimers.current.get(id))
			toastTimers.current.delete(id)
			pausedToasts.current.delete(id)
			updateToasts(current => current.map(toast => (toast.id === id ? { ...toast, isExiting: true } : toast)))

			setTimeout(() => {
				updateToasts(current => current.filter(toast => toast.id !== id))
			}, 500)
		},
		[updateToasts],
	)

	const scheduleDismiss = useCallback(
		(id: string, type: "success" | "error") => {
			clearTimeout(toastTimers.current.get(id))
			toastTimers.current.delete(id)
			if (pausedToasts.current.has(id)) return
			toastTimers.current.set(
				id,
				setTimeout(() => dismissToast(id), toastDurations[type]),
			)
		},
		[dismissToast],
	)

	const pauseDismiss = useCallback((id: string, reason: "hover" | "focus") => {
		clearTimeout(toastTimers.current.get(id))
		toastTimers.current.delete(id)
		const reasons = pausedToasts.current.get(id) ?? new Set()
		reasons.add(reason)
		pausedToasts.current.set(id, reasons)
	}, [])

	const resumeDismiss = useCallback(
		(id: string, type: "success" | "error", reason: "hover" | "focus") => {
			const reasons = pausedToasts.current.get(id)
			reasons?.delete(reason)
			if (reasons?.size === 0) pausedToasts.current.delete(id)
			const toast = toastsRef.current.find(toast => toast.id === id)
			if (toast !== undefined && !toast.isExiting) scheduleDismiss(id, type)
		},
		[scheduleDismiss],
	)

	const createToast = useCallback(
		(type: "success" | "error", text: string) => {
			announce(type === "error" ? "assertive" : "polite", text)

			const visible = toastsRef.current.filter(toast => !toast.isExiting)
			const duplicate = visible.find(toast => toast.type === type && toast.text === text)
			if (duplicate !== undefined) {
				scheduleDismiss(duplicate.id, type)
				return
			}

			const id = randomUUID()
			updateToasts(current => [...current, { id, text, type }])
			scheduleDismiss(id, type)
			for (const toast of visible.slice(0, Math.max(visible.length + 1 - maxVisibleToasts, 0)))
				dismissToast(toast.id)
		},
		[announce, scheduleDismiss, dismissToast, updateToasts],
	)

	useEffect(() => {
		if (isUnsavedChangesOpen) announce("polite", "You have unsaved changes")
	}, [isUnsavedChangesOpen, announce])

	const handleShowChangesSaved = useCallback(
		(changeName?: string) => {
			createToast("success", `${changeName ? `${changeName} was` : "Changes were"} saved successfully`)
		},
		[createToast],
	)

	const value = useMemo<ToastContextValue>(
		() => ({
			showChangesSaved: handleShowChangesSaved,
			showPublished(productName) {
				createToast("success", `${productName} was published successfully`)
			},
			showDeleted(deletedName) {
				createToast("success", `${deletedName} was deleted successfully`)
			},
			showError(error) {
				createToast("error", error)
			},
			showSuccess(success) {
				createToast("success", success)
			},
			setUnsavedChanges(onSave) {
				openUnsavedChanges({ onSave })
			},
			setDiscardableUnsavedChanges({ saveLabel, onSave, onDiscard }) {
				openUnsavedChanges({ onSave, onDiscard }, saveLabel)
			},
			clearUnsavedChanges,
		}),
		[createToast, handleShowChangesSaved, clearUnsavedChanges, openUnsavedChanges],
	)

	return (
		<ToastContext.Provider value={value}>
			<div className="sr-only" aria-live="polite" aria-atomic="true">
				{announcements.polite}
			</div>
			<div className="sr-only" aria-live="assertive" aria-atomic="true">
				{announcements.assertive}
			</div>
			<Container $hasToasts={unsavedChangesData?.isOpen || toasts.some(x => !x.isExiting)}>
				{toasts.map(toast => (
					<Toast
						key={toast.id}
						$type={toast.type}
						$isExiting={toast.isExiting ?? false}
						// Hovering or focusing a toast holds it open until the pointer or focus leaves.
						onMouseEnter={() => pauseDismiss(toast.id, "hover")}
						onMouseLeave={() => resumeDismiss(toast.id, toast.type, "hover")}
						onFocus={() => pauseDismiss(toast.id, "focus")}
						onBlur={() => resumeDismiss(toast.id, toast.type, "focus")}
					>
						<ToastText>{toast.text}</ToastText>
						{toast.type === "error" && (
							<DismissButton
								type="button"
								aria-label="Dismiss"
								onClick={() => dismissToast(toast.id)}
								disabled={toast.isExiting}
							>
								<CloseIcon className="size-5" />
							</DismissButton>
						)}
					</Toast>
				))}
				{unsavedChangesData && (
					<UnsavedChanges
						data={unsavedChangesData}
						handlers={unsavedChangesHandlers}
						setData={setUnsavedChangesData}
						clearUnsavedChanges={clearUnsavedChanges}
					/>
				)}
			</Container>
			<UnsavedChangesOpenContext.Provider value={isUnsavedChangesOpen}>
				{children}
			</UnsavedChangesOpenContext.Provider>
		</ToastContext.Provider>
	)
}

type UnsavedChangesProps = {
	data: UnsavedChangesData
	handlers: RefObject<UnsavedChangesHandlers | null>
	setData: Dispatch<SetStateAction<UnsavedChangesData | undefined>>
	clearUnsavedChanges(): void
}
const UnsavedChanges: FC<UnsavedChangesProps> = ({ data, handlers, setData, clearUnsavedChanges }) => {
	async function handleSave() {
		if (handlers.current === null) return
		setData(current => (current ? { ...current, isSaving: true } : undefined))

		const result = await handlers.current.onSave()
		if (!result) {
			setData(current => (current?.isSaving ? { ...current, isSaving: false } : current))
			return
		}

		clearUnsavedChanges()

		setTimeout(() => {
			setData(current => (current?.isSaving ? { ...current, isSaving: false } : current))
		}, 500)
	}

	function handleDiscard() {
		handlers.current?.onDiscard?.()
		clearUnsavedChanges()
	}

	return (
		<Toast key="unsaved" $type="unsaved" $isExiting={!data.isOpen}>
			<span>You have unsaved changes</span>
			<Buttons>
				<Button variant="primary" isLoading={data.isSaving} onClick={data.isOpen ? handleSave : undefined}>
					{data.saveLabel ?? "Save changes"}
				</Button>
				{data.canDiscard && (
					<Button variant="secondary-danger" onClick={data.isOpen ? handleDiscard : undefined}>
						Discard changes
					</Button>
				)}
			</Buttons>
		</Toast>
	)
}

const Container = styled.div<{ $hasToasts: boolean }>`
	position: fixed;
	bottom: 16px;
	left: 50%;
	transform: translateX(-50%);
	width: min(600px, calc(100vw - 32px));

	@media (min-width: 640px) {
		bottom: 42px;
	}
	display: flex;
	flex-direction: column;
	gap: 12px;
	z-index: 1000;

	${props =>
		!props.$hasToasts &&
		css`
			pointer-events: none;
		`}
`

const enterAnimation = keyframes`
	from {
		opacity: 0;
		transform: translateY(100%);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`

const exitAnimation = keyframes`
	from {
		opacity: 1;
		transform: translateY(0);
	}
	to {
		opacity: 0;
		transform: translateY(100%);
	}
`

const Toast = styled.div<{ $isExiting: boolean; $type: "success" | "unsaved" | "error" }>`
	font-size: 16px;
	font-weight: 500;
	box-shadow:
		0 4px 8px rgba(0, 0, 0, 0.1),
		0 2px 4px rgba(0, 0, 0, 0.1);
	padding: 12px 24px;
	border-radius: 8px;
	opacity: 0;
	transform: translateY(100%);
	animation: ${props => (props.$isExiting ? exitAnimation : enterAnimation)} 0.5s forwards;
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	align-items: center;
	gap: 12px 16px;
	overflow-wrap: anywhere;

	${props =>
		props.$type === "success"
			? css`
					color: white;
					background-color: ${successColor};
				`
			: props.$type === "unsaved"
				? css`
						color: ${textColor};
						background-color: white;
						border: 1px solid ${componentBorderColor};
					`
				: props.$type === "error"
					? css`
							color: white;
							background-color: ${dangerColor};
						`
					: null}
`

const Buttons = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	align-items: center;
`

// Takes the remaining width so the dismiss button stays beside the message instead of wrapping below it.
const ToastText = styled.span`
	flex: 1 1 0;
	min-width: 0;
`

const DismissButton = styled.button`
	flex-shrink: 0;
	margin-right: -8px;
	padding: 4px;
	border-radius: 6px;
	cursor: pointer;
	color: inherit;

	&:hover {
		background-color: rgb(255 255 255 / 0.15);
	}

	&:focus-visible {
		outline: 2px solid white;
		outline-offset: 2px;
	}
`

const Button = styled(BaseButton)`
	white-space: nowrap;
`

export default ToastContextProvider
